/**
 * useAddressLines — lignes de la carte « Lieu » des pages publiques
 * /a/[slug] et /b/[slug].
 *
 * Lot 4 (2026-09-27, contrat docs/architecture/adresses-structurees.md §10 :
 * « `formaterLieu` partout ») — la carte suit la MÊME règle que l'aperçu du
 * dashboard (`ligneLieu(…, { avecNom: false })`), la fiche mobile
 * (`adresseUneLigne`), les emails et PDF de l'API (`affichageLieu`) et
 * /b/[slug]/success :
 *   1. le NOM du lieu, à part (1re ligne, en gras) ;
 *   2. le lieu en une ligne, `formaterLieu({ adresse, ville, structure })` :
 *      adresse, ville si l'adresse ne la contient pas déjà, puis territoire
 *      ou pays hors métropole (« Rue Victor Hugo, Sainte-Luce, Martinique »).
 * Plus de règle locale (l'ancienne ajoutait une ligne « CP Ville » vue
 * nulle part ailleurs : l'aperçu du dashboard ne montrait pas la page).
 *
 * Doublons (Karim 2026-07-08) : les champs sont saisis librement et se
 * chevauchent souvent. La ville déjà dans l'adresse est retirée par
 * `formaterLieu` ; un nom de lieu qui ne fait que répéter l'adresse (nom =
 * « 12 rue X », adresse = « 12 rue X 31840 Ville ») n'est pas réaffiché —
 * décidé lui aussi par `formaterLieu` (règle « nom == rue » : slug égal ou
 * contenu en mots entiers), plus aucune comparaison locale.
 *
 * Sans morceaux (anciennes lignes, API d'avant 307) : pas de territoire ni de
 * pays, le reste est inchangé.
 */
import { formaterLieu, replierBlancs } from "@donoor/utils";
import type { ChampsLieuStructure } from "@donoor/types";

interface AddressFields extends ChampsLieuStructure {
  location_name?: string | null;
  location_address?: string | null;
  location_city?: string | null;
}

/**
 * Lignes à afficher : [nom du lieu, lieu en une ligne], sans vide ni
 * répétition. Résultat vide = pas d'adresse.
 */
export function buildAddressLines(fields: AddressFields): string[] {
  const nom = replierBlancs(fields.location_name);
  const structure =
    fields.location_country || fields.location_territory
      ? { pays: fields.location_country ?? null, territoire: fields.location_territory ?? null }
      : null;
  const lieu = formaterLieu({ adresse: fields.location_address, ville: fields.location_city, structure });
  if (!lieu) {
    const seul = formaterLieu({ nom });
    return seul ? [seul] : [];
  }
  // Nom retiré par la règle partagée (il ne fait que répéter le lieu) : la
  // ligne complète est alors le lieu seul.
  if (!nom || formaterLieu({ nom, adresse: lieu }) === lieu) return [lieu];
  return [nom, lieu];
}
